import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import apiClient from "../api/apiClient";

// Types
export interface Permission {
  id: number;
  name: string;
  description: string;
}

export interface RolePermission {
  id: number;
  role_id: number;
  permission_id: number;
  status: number;
  created_at: string;
  updated_at: string;
  deleted_at: null | string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  total_users?: number;
  permissions: RolePermission[];
}

interface PaginatedResponse<T> {
  status: boolean;
  message: string;
  data: T[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: Array<{
      url: string | null;
      label: string;
      active: boolean;
    }>;
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
}
// ===========================================
// PERMISSION HOOKS
// ===========================================

// Fetch all permissions

// Fetch all permissions with pagination support
export const usePermissions = (page = 1, perPage = 100) => {
  return useQuery<PaginatedResponse<Permission>, Error>({
    queryKey: ["permissions", page, perPage],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<Permission>>(
        `/permission?page=${page}&per_page=${perPage}`
      );
      return response.data;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};

// Fetch ALL permissions by automatically fetching all pages
export const useAllPermissions = () => {
  // const queryClient = useQueryClient();

  // First fetch the first page to get metadata about pagination
  return useQuery<Permission[], Error>({
    queryKey: ["allPermissions"],
    queryFn: async () => {
      // First get page 1 to see how many pages there are
      const firstPageResponse = await apiClient.get<
        PaginatedResponse<Permission>
      >("/permission?page=1");
      const firstPageData = firstPageResponse.data;

      // If there's only one page, return the data
      if (firstPageData.meta.last_page === 1) {
        return firstPageData.data;
      }

      // Otherwise, we need to fetch all pages
      const totalPages = firstPageData.meta.last_page;
      const allPermissions: Permission[] = [...firstPageData.data];

      // Fetch the remaining pages in parallel
      const pagePromises = [];
      for (let page = 2; page <= totalPages; page++) {
        pagePromises.push(
          apiClient.get<PaginatedResponse<Permission>>(
            `/permission?page=${page}`
          )
        );
      }

      const pageResponses = await Promise.all(pagePromises);

      // Combine the data from all pages
      pageResponses.forEach((response) => {
        allPermissions.push(...response.data.data);
      });

      return allPermissions;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};

// Create a new permission
export const useCreatePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newPermission: {
      name: string;
      description: string;
    }) => {
      await apiClient.post("/permission/create", {
        name: newPermission.name,
        description: newPermission.description,
      });
    },
    onSuccess: () => {
      message.success("Permission created successfully!");
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
    onError: () => {
      message.error("Failed to create permission. Please try again.");
    },
  });
};

// Update an existing permission
export const useUpdatePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      permissionId,
      updatedPermission,
    }: {
      permissionId: number;
      updatedPermission: { name: string; description: string };
    }) => {
      await apiClient.put(`/permission/${permissionId}`, {
        name: updatedPermission.name,
        description: updatedPermission.description,
      });
    },
    onSuccess: () => {
      message.success("Permission updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
    onError: () => {
      message.error("Failed to update permission. Please try again.");
    },
  });
};

// Delete a permission
export const useDeletePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (permissionId: number) => {
      await apiClient.delete(`/permission/${permissionId}`);
    },
    onSuccess: () => {
      message.success("Permission deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
      queryClient.invalidateQueries({ queryKey: ["roles"] }); // Refresh roles as well since permissions might be linked
    },
    onError: () => {
      message.error("Failed to delete permission. Please try again.");
    },
  });
};

// ===========================================
// ROLE HOOKS
// ===========================================

// Fetch all roles with their permissions
export const useRoles = () => {
  return useQuery<Role[], Error>({
    queryKey: ["roles"],
    queryFn: async () => {
      const response = await apiClient.get<{ status: boolean; data: Role[] }>(
        "/role"
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};

export const useToggleRolePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rolePermissionId,
      role_id,
      permission_id,
      status,
      isNew = false,
    }: {
      rolePermissionId?: number;
      role_id: number;
      permission_id: number;
      status: number;
      isNew?: boolean;
    }) => {
      // For new role-permission relationships
      if (isNew) {
        await apiClient.post("/role-permission/create", {
          role_id,
          permission_id,
          status,
        });
      } else {
        // For existing relationships
        await apiClient.put(`/role-permission/${rolePermissionId}`, {
          permission_id,
          status,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (error) => {
      console.error("Permission update error:", error);
      message.error("Failed to update permission. Please try again.");
    },
  });
};

// Create a new role
export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newRole: { name: string; description: string }) => {
      await apiClient.post("/role/create", newRole);
    },
    onSuccess: () => {
      message.success("Role created successfully!");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: () => {
      message.error("Failed to create role. Please try again.");
    },
  });
};

// Update an existing role
export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      roleId,
      updatedRole,
    }: {
      roleId: number;
      updatedRole: { name: string; description: string };
    }) => {
      await apiClient.put(`/role/${roleId}`, updatedRole);
    },
    onSuccess: () => {
      message.success("Role updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: () => {
      message.error("Failed to update role. Please try again.");
    },
  });
};

// Delete a role
export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleId: number) => {
      await apiClient.delete(`/role/${roleId}`);
    },
    onSuccess: () => {
      message.success("Role deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: () => {
      message.error("Failed to delete role. Please try again.");
    },
  });
};
